import AOS from "aos";
import "aos/dist/aos.css";
import { auth, provider, signInWithPopup } from "./firebase-config";
import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { Link, useNavigate } from "react-router-dom";
import AuthApiService from "../../ApiServices/AuthApiService";
import SigninWithGoogleRequest from "../../Models/SignInWithGoogleRequest";
import { useAuth } from "./AuthContext";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";
import { Typography } from "@material-tailwind/react";
import { toast } from "react-toastify";


export default function Login() {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { setAuthInfo } = useAuth();

  // 🔹 Google Sign-In
  const handleGoogleSignIn = async () => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    document.cookie = `clientTimeZone=${encodeURIComponent(timeZone)}; path=/;`;

    setLoading(true);
    setErrorMessage("");

    try {
      const result = await signInWithPopup(auth, provider);

      const user = result.user;
      const credentials = {
        username: user.email,
        password: "GoogleOAuth", // Optional placeholder for Google login
        rememberMe: true,
      };

      const UserDetail = await AuthApiService.checkUserByEmail(credentials);

      if (UserDetail && UserDetail.isSuccess) {
        const user = UserDetail.data.user;
        const role = UserDetail.data.roles;
        setAuthInfo(user, role);
        navigate("/home");
      } else {
        const signInWithGoogleRequest = new SigninWithGoogleRequest(
          result.user.email,
          result._tokenResponse.firstName,
          result._tokenResponse.lastName
        );

        await AuthApiService.signInWithGoogle(signInWithGoogleRequest);
        navigate("/signup", { state: signInWithGoogleRequest });
        alert("Please fill in the details and then log in.");
      }
    } catch (error) {
      setErrorMessage("Failed to sign in. Please try again.");
      console.error("Error during sign-in:", error);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Manual Sign-In
  const handleSignIn = async (e) => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    document.cookie = `clientTimeZone=${encodeURIComponent(timeZone)}; path=/;`;

    e.preventDefault(); // Prevent form submission
    setLoading(true);
    setErrorMessage("");

    try {
      const credentials = { username: email, password, rememberMe: true };
      const result = await AuthApiService.signin(credentials);
      if (result.isSuccess) {
        const user = result.data.user;
        const role = result.data.roles;
        const token = result.data.token;
        localStorage.setItem("token", token);
        setAuthInfo(user, role);
        navigate("/home");
      } else {
        toast.error("Login failed. Please check your email or password");
        setErrorMessage("Login failed. Please check your credentials.");
      }
    } catch (error) {
      setErrorMessage("Failed to sign in. Please try again.");
      console.error("Error during sign-in:", error);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
  };

  const images = [
    "/Images/Login/dashboard1.jpeg",
    "/Images/Login/dashboard2.jpeg",
    "/Images/Login/dashboard3.jpeg",
  ]; // Replace with actual images


  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-6 bg-cover bg-center"
      style={{ backgroundImage: "url('/Images/Logos/background.jpg')" }}
    >
      <div className="flex flex-wrap w-11/12 max-w-8xl justify-center gap-10">
        {/* Left Side - Slider Section */}
        <div className="w-full md:w-[60%] h-[85vh] bg-white shadow-lg rounded-lg p-6">
          <div className="flex flex-col items-center">
            <Slider {...sliderSettings} className="w-full max-w-lg h-full">
              {images.map((image, index) => (
                <div key={index} className="flex justify-center w-full">
                  <img
                    src={image}
                    alt={`Slide ${index + 1}`}
                    className="w-full h-[65vh] rounded-lg shadow-md object-cover"
                  />
                </div>
              ))}
            </Slider>
            {/* Text Below Slider */}
            <div className="mt-6 text-center px-4">
              <h1 className="text-xl font-bold text-gray-800">
                Access to a large pool of advertisers:
              </h1>
              <p className="text-gray-600 mt-2">
                Adx has strict criteria for advertisers, which can lead to more
                competition and higher revenue per ad impression.
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Form Section */}
        <div className="w-full md:w-[35%] bg-white shadow-lg rounded-lg p-6">
          <div className="flex justify-center items-center bg-gray-100">
            <img
              src="./Images/logos/weblogo.png"
              className="h-16 md:h-20 lg:h-24 object-contain max-w-full"
              alt="Google Partner"
            />
          </div>
          <div className="max-w-md w-full mx-auto">
            <h1 className="text-3xl font-bold text-gray-800">
              Sign in to your account
            </h1>
            <p className="text-sm text-gray-500 font-medium mt-2">
              Don't have an account?{" "}
              <Link to="/signup" className="text-customColor hover:underline">
                Sign Up
              </Link>
            </p>
            <button
              onClick={handleGoogleSignIn}
              className="mt-6 flex items-center justify-center w-full bg-white border border-customColor rounded-lg shadow-sm px-4 py-2 hover:bg-gray-50 transition"
            >
              <img src="/Images/gog.png" alt="Google" className="h-5 w-5 mr-2" />
              <span className="text-customColor font-medium">
                Sign In with Google
              </span>
            </button>
            <div className="relative mt-6 text-center">
              <span className="absolute inset-x-0 top-1/2 transform -translate-y-1/2 bg-white px-4 text-gray-500">
                Or Use Email
              </span>
              <div className="h-px bg-gray-300"></div>
            </div>
            <form className="space-y-4 mt-6" onSubmit={handleSignIn}>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email <span className="text-orange-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:outline-none"
                  placeholder="abc@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Password <span className="text-orange-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:outline-none"
                    placeholder="••••••••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-3 flex items-center"
                  >
                    <FontAwesomeIcon
                      icon={showPassword ? faEyeSlash : faEye}
                      className="w-5 h-5 text-gray-500"
                    />
                  </button>
                </div>
              </div>
              <div className="text-left">
                <Link
                  to="/forgot-password"
                  className="text-lg text-customColor hover:underline font-bold"
                >
                  Forgot Password?
                </Link>
              </div>
              <button
                type="submit"
                className="w-full bg-black text-white py-3 rounded-lg hover:bg-purple-800 transition"
              >
                {loading ? "Loading..." : "Continue"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );

}
